import { z } from 'zod';
import { stringify } from 'yaml';
import type { JSONArray, JSONValue } from '@sisense/sdk-data';
import { translateQueryToJSON } from '@sisense/sdk-ui/analytics-composer';
import {
	getDataSourceFieldsWithDescriptions,
	DataSourceFieldWithDescription,
} from '@/services/data-sources/data-sources-service';
import { getFormulasByDataSource } from '@/services/formulas/formulas-service';
import { DataSourceField } from '@sisense/sdk-data';
import {
	MAX_DATA_SOURCE_FIELDS_SUMMARY,
	MAX_DATA_SOURCE_FORMULAS_SUMMARY,
	TOOL_NAME_GET_DATA_SOURCE_FIELDS,
	toEngineName,
} from '@/constants';
import { getHttpErrorStatus } from '@/utils/http-client';
import {
	getDataSourceFieldsSchema,
	GetDataSourceFieldsContext,
	DataSourceFieldsResult,
} from './types';
import { devDebug } from '@/utils/dev-debug';

/**
 * Fetches fields for a data source (decoupled from UI/store).
 * @param params - The parameters containing the data source title
 * @param context - Context with tool call ID for tracking
 * @returns Raw result: dataSourceTitle and fields array for UI and for building the LLM summary.
 */
export async function getDataSourceFieldsEngine(
	params: z.infer<typeof getDataSourceFieldsSchema>,
	context: GetDataSourceFieldsContext,
): Promise<DataSourceFieldsResult> {
	const { dataSourceTitle } = params;

	devDebug('engineCall', {
		engine: toEngineName(TOOL_NAME_GET_DATA_SOURCE_FIELDS),
		dataSourceTitle,
		toolCallId: context.toolCallId,
	});

	if (!dataSourceTitle) {
		throw new Error(
			'Error: Please ask the user to specify a data source title',
		);
	}

	const httpClient = context.httpClient;

	const [fields, calculatedMeasures] = await Promise.all([
		getDataSourceFieldsWithDescriptions(dataSourceTitle, httpClient),
		getFormulasByDataSource(dataSourceTitle, httpClient).catch((err) => {
			const status =
				getHttpErrorStatus(err) ??
				getHttpErrorStatus((err as Error).cause);
			if (status === 401 || status === 403 || status === 404) {
				return [];
			}
			throw err;
		}),
	]);

	let formulas: JSONArray = [];
	const translationResult = translateQueryToJSON({
		measures: calculatedMeasures,
		dimensions: [],
		filters: [],
	});
	if (
		translationResult.success &&
		translationResult.data?.measures &&
		Array.isArray(translationResult.data.measures)
	) {
		formulas = translationResult.data.measures;
	}

	return {
		dataSourceTitle,
		fields: fields || [],
		formulas,
	};
}

function buildCompactSchema(
	dataSourceTitle: string,
	message: string,
	isTruncated: boolean,
	tables: Record<string, string[]>,
): string {
	const truncationNote = isTruncated
		? ' (truncated summary from the data source)'
		: '';
	const intro = `${dataSourceTitle}: ${message}${truncationNote}.`;
	const tableEntries = Object.keys(tables);
	if (tableEntries.length === 0) {
		return intro;
	}
	const tablesYaml = stringify(tables, {
		defaultStringType: 'PLAIN',
	}).trimEnd();
	return `${intro}\n\n${tablesYaml}`;
}

/**
 * Builds the shared formulas block for the LLM summary from NLQ-shaped formula JSON.
 * Each formula must have an `args` array: name = args[0]; description = args[args.length - 1] only when args.length >= 2, else ''.
 * @param formulas - Shared formulas in JSON format (array of { function, args })
 * @returns YAML block with heading and list of { name, description }, or empty string if none
 */
export function buildCompactSharedFormulas(formulas: JSONArray): string {
	if (!formulas?.length) {
		return '';
	}
	const truncatedFormulas = formulas.slice(
		0,
		MAX_DATA_SOURCE_FORMULAS_SUMMARY,
	);
	const totalFormulas = formulas.length;
	const formulasTruncated = totalFormulas > MAX_DATA_SOURCE_FORMULAS_SUMMARY;
	const formulaEntries = truncatedFormulas.map(
		(item: JSONValue, i: number) => {
			const args =
				item !== null &&
				typeof item === 'object' &&
				'args' in item &&
				Array.isArray(item.args)
					? item.args
					: undefined;
			const first = args?.[0];
			const last = args?.length ? args[args.length - 1] : undefined;
			const description =
				args && args.length >= 2 && typeof last === 'string'
					? last
					: '';
			return {
				name: typeof first === 'string' ? first : `formula_${i + 1}`,
				description,
			};
		},
	);
	const formulasLabel = totalFormulas === 1 ? 'formula' : 'formulas';
	const formulasHeading = formulasTruncated
		? `Shared ${formulasLabel} (${totalFormulas} total, showing first ${MAX_DATA_SOURCE_FORMULAS_SUMMARY})`
		: `Shared ${formulasLabel} (${totalFormulas})`;
	return stringify(
		{ [formulasHeading]: formulaEntries },
		{ defaultStringType: 'PLAIN' },
	).trimEnd();
}

/**
 * Builds the compact schema string returned to the LLM (intro + tables/columns + optional shared formulas).
 * Reduces token use by returning a single string instead of a JSON object.
 * @param dataSourceTitle - The title of the data source
 * @param fields - The full list of fields from the data source
 * @param formulas - Optional shared formulas in NLQ JSON format (for the chat agent)
 * @returns A compact schema string with table and column names, and shared formulas when provided
 */
export function getDataSourceFieldsSummary(
	dataSourceTitle: string,
	fields: DataSourceFieldWithDescription[] | DataSourceField[],
	formulas?: JSONArray,
): string {
	const totalFields = fields.length;
	const tableCount = new Set(fields.map((f) => f.table)).size;
	const truncatedFields = fields.slice(0, MAX_DATA_SOURCE_FIELDS_SUMMARY);
	const isTruncated = totalFields > MAX_DATA_SOURCE_FIELDS_SUMMARY;

	const tables: Record<string, string[]> = {};
	for (const field of truncatedFields) {
		const tableName = field.table;
		if (!tables[tableName]) tables[tableName] = [];
		tables[tableName].push(field.column);
	}

	const fieldsLabel = totalFields === 1 ? 'field' : 'fields';
	const tablesLabel = tableCount === 1 ? 'table' : 'tables';
	const message = isTruncated
		? `Retrieved ${totalFields} ${fieldsLabel} from ${tableCount} ${tablesLabel} (showing first ${MAX_DATA_SOURCE_FIELDS_SUMMARY} in summary)`
		: `Retrieved ${totalFields} ${fieldsLabel} from ${tableCount} ${tablesLabel}`;

	let out = buildCompactSchema(dataSourceTitle, message, isTruncated, tables);

	const formulasBlock = buildCompactSharedFormulas(formulas ?? []);
	if (formulasBlock) {
		out = `${out}\n\n${formulasBlock}`;
	}

	return out;
}
